import { getIdToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';

const getCloudFunctionUrl = (functionName) => {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'market-genie-f2d41';
  return `https://us-central1-${projectId}.cloudfunctions.net/${functionName}`;
};

const callCloudFunctionFallback = async (functionName, payload = {}) => {
  const url = getCloudFunctionUrl(functionName);
  const headers = { 'Content-Type': 'application/json' };

  if (auth?.currentUser) {
    try {
      const idToken = await getIdToken(auth.currentUser, true);
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`;
      }
    } catch (tokenError) {
      console.warn('⚠️ Failed to refresh auth token for fallback callable:', tokenError);
    }
  }

  const callablePayload = { data: payload };
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(callablePayload)
  });

  const responseBody = await response.json();
  if (!response.ok) {
    throw new Error(responseBody?.error || responseBody?.message || `HTTP ${response.status}`);
  }

  return { data: responseBody?.result ?? responseBody };
};

class StripeConnectService {
  async createConnectAccountLink({ tenantId, userId, email, companyName, returnUrl, refreshUrl }) {
    if (!tenantId || !userId || !email) {
      throw new Error('tenantId, userId, and email are required to create Stripe Connect account link');
    }

    const payload = {
      tenantId,
      userId,
      email,
      companyName,
      returnUrl,
      refreshUrl
    };

    console.log('🔗 Stripe connect payload:', payload);

    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'market-genie-f2d41';
    const url = `https://us-central1-${projectId}.cloudfunctions.net/createStripeConnectAccountLinkHttp`;
    const headers = {
      'Content-Type': 'application/json'
    };

    try {
      if (auth?.currentUser) {
        const idToken = await getIdToken(auth.currentUser, true);
        if (idToken) {
          headers.Authorization = `Bearer ${idToken}`;
        }
      }
    } catch (tokenError) {
      console.warn('⚠️ Failed to refresh auth token for Stripe connect request:', tokenError);
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      let responseBody = null;

      try {
        responseBody = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`Stripe Connect response is not valid JSON: ${responseText}`);
      }

      if (!response.ok) {
        throw new Error(responseBody?.error || responseBody?.message || `HTTP ${response.status}`);
      }

      if (!responseBody?.success) {
        throw new Error(responseBody?.error || 'Unable to create Stripe Connect account link');
      }

      return responseBody;
    } catch (error) {
      console.error('❌ Stripe connect HTTP request failed:', {
        url,
        payload,
        errorMessage: error?.message,
        error
      });
      throw new Error(error?.message || 'Unable to create Stripe Connect account link');
    }
  }

  async getStripeConnectAccountStatus({ userId }) {
    const resolvedUserId = userId || auth?.currentUser?.uid || null;
    console.log('🔍 StripeConnectService.getStripeConnectAccountStatus', { userId, resolvedUserId, currentUserUid: auth?.currentUser?.uid });

    if (!resolvedUserId) {
      throw new Error('userId is required to get Stripe Connect account status');
    }

    let response;
    if (typeof httpsCallable === 'function' && functions) {
      const getStatus = httpsCallable(functions, 'getStripeConnectAccountStatus');
      response = await getStatus({ userId: resolvedUserId });
    } else {
      console.warn('⚠️ httpsCallable unavailable, using fallback HTTP callable for getStripeConnectAccountStatus');
      response = await callCloudFunctionFallback('getStripeConnectAccountStatus', { userId: resolvedUserId });
    }

    if (!response?.data?.success) {
      throw new Error(response?.data?.error || 'Unable to retrieve Stripe Connect account status');
    }

    return response.data;
  }

  async createPartnerCheckoutSession({ partnerTenantId, customerEmail, lineItems, successUrl, cancelUrl, metadata = {} }) {
    console.log('🔧 StripeConnectService.createPartnerCheckoutSession called', {
      partnerTenantId,
      customerEmail: customerEmail ? String(customerEmail).slice(0, 5) + '...' : null,
      lineItemsCount: Array.isArray(lineItems) ? lineItems.length : 0,
      successUrl,
      cancelUrl
    });

    if (!partnerTenantId || !lineItems || !successUrl || !cancelUrl) {
      throw new Error('partnerTenantId, lineItems, successUrl, and cancelUrl are required');
    }

    let response;
    if (typeof httpsCallable === 'function' && functions) {
      const createSession = httpsCallable(functions, 'createStripeCheckoutSession');
      response = await createSession({
        partnerTenantId,
        customerEmail,
        lineItems,
        successUrl,
        cancelUrl,
        metadata
      });
    } else {
      const payload = {
        partnerTenantId,
        customerEmail,
        lineItems,
        successUrl,
        cancelUrl,
        metadata
      };
      console.warn('⚠️ httpsCallable unavailable, using fallback HTTP callable for createStripeCheckoutSession', { payload });
      response = await callCloudFunctionFallback('createStripeCheckoutSession', payload);
    }

    if (!response?.data?.success) {
      throw new Error(response?.data?.error || 'Unable to create Stripe checkout session');
    }

    return response.data;
  }

  async getStripeCheckoutSessionMetadata(sessionId) {
    if (!sessionId) {
      throw new Error('sessionId is required');
    }

    let response;
    if (typeof httpsCallable === 'function' && functions) {
      const getMetadata = httpsCallable(functions, 'getStripeCheckoutSessionMetadata');
      response = await getMetadata({ sessionId });
    } else {
      response = await callCloudFunctionFallback('getStripeCheckoutSessionMetadata', { sessionId });
    }

    if (!response?.data?.success) {
      throw new Error(response?.data?.error || 'Unable to get Stripe session metadata');
    }

    return response.data;
  }
}

export const stripeConnectService = new StripeConnectService();
export default stripeConnectService;
